import {action, makeAutoObservable} from "mobx";
import PaymentType from "../../../entities/PaymentType";
import AddPaymentRequest from "../../../model/payment/AddPaymentRequest";
import Currency from "../../../entities/Currency";
import PaymentTypeResponse from "../../../model/payment/PaymentTypeResponse";
import OrderAdditionalPrice from "../../../entities/OrderAdditionalPriceRequest";
import OrderPaymentResponse from "../../../model/payment/OrderPaymentResponse";

class AddPaymentStore {

    public payments: OrderPaymentResponse[];
    public additionalPrices: OrderAdditionalPrice[];
    public selectedPrice: number;
    public error?: string;
    public total: number;
    public selectedPaymentTypes: PaymentTypeResponse[];
    public selectedPaymentType?: PaymentType;

    public userId?: string;


    constructor(
        total: number,
        currentAdditionalPrices: OrderAdditionalPrice[],
        userId?: string,
        currentPayments?: OrderPaymentResponse[],
    ) {
        this.total = total;
        this.userId = userId;
        this.payments = [];
        this.additionalPrices = [...currentAdditionalPrices];
        this.selectedPaymentTypes = [];
        this.selectedPrice = this.getRemaining;
        if(currentPayments){
            this.addAllPayments(currentPayments)
        }
        makeAutoObservable(this);
    }
    
    @action
    private addAllPayments(allPayments: OrderPaymentResponse[]){
        allPayments.forEach(element => {
            const payment = new OrderPaymentResponse(element.amount, element.currency, element.paymentTypeResponse, element.createdAt, element.userId, element.isEditable);
            this.payments.push(payment);
        })
        this.selectedPrice = this.getRemaining;
        if (this.getRemaining < 0) {
            this.error = "OLD_OVERPAYMENT";
            this.selectedPrice = this.getRemaining;
            return;
        }
    }

    @action
    public addPayment() {
        if (this.getRemaining < this.selectedPrice) {
            this.error = "OVERPAYMENT";
            this.selectedPrice = this.getRemaining;
            return;
        }
        if (this.selectedPaymentTypes.length === 0) {
            this.error = "SELECT_PAYMENT_TYPE";
            return;
        }
        const tempSelectedPaymentType = this.selectedPaymentTypes.find(t => t.fromValue <= this.selectedPrice && t.toValue >= this.selectedPrice) || this.selectedPaymentTypes[0];
        const current = this.payments.find(t =>
            t.paymentTypeResponse.paymentType === tempSelectedPaymentType.paymentType && t.isEditable && t.userId === this.userId
        );
        if (current) {
            const newAmount = current.amount + this.selectedPrice;
            const newPaymentType  = this.selectedPaymentTypes.find(t => t.fromValue <= newAmount && t.toValue >= newAmount) || this.selectedPaymentTypes[0];
            current.paymentTypeResponse = newPaymentType;
            current.amount = newAmount;
            this.payments = [...this.payments];
        } else {
            const payment = new OrderPaymentResponse(this.selectedPrice, Currency.DOLLAR, tempSelectedPaymentType, 0, this.userId ?? '', true);
            this.payments.push(payment);
        }
        this.error = undefined;
        this.selectedPrice = this.getRemaining;
        this.selectedPaymentTypes = [];
        this.selectedPaymentType = undefined;
    }

    @action
    public removePayment(payment: AddPaymentRequest) {
        const payments = this.payments.filter(t => t.paymentTypeResponse !== payment.paymentTypeResponse);
        this.payments = [...payments];
        const additionalPrices = this.additionalPrices.filter(t => t.paymentType !== payment.paymentType);
        this.additionalPrices = [...additionalPrices];
        this.error = undefined;
        this.selectedPrice = this.getRemaining;
    }

    get isEmpty(): boolean {
        return this.payments.length === 0;
    }

    get getRemaining(): number {
        return this.total - this.getPaymentsSum;
    }

    get getPaymentsSum(): number {
        return this.payments.reduce((a, i) => a + i.amount, 0);
    }

    get getFeesSum(): number {
        return this.payments.reduce((a, i) => a + i.getExtraFee, 0);
    }

    get getTotalToPay(): number {
        return (this.total) + this.getFeesSum + this.getAdditionalPriceSum;
    }

    get getAdditionalPriceSum(): number {
        return this.additionalPrices.reduce((sum, item) => {
            if (item.isPercentage) {
                return sum + (item.amount / 100) * this.total;
            } else {
                return sum + item.amount;
            }
        }, 0);
    }

    get canAddMorePayments(): boolean {
        return this.getPaymentsSum < this.total;
    }

    get paymentTypeSelected(): boolean {
        return this.selectedPaymentTypes.length > 0;
    }

    // Additional prices

    @action
    public addAdditionalPrice(amount: number, comment: string, isPercentage: boolean, paymentType: PaymentType) {
        const additionalPrice = new OrderAdditionalPrice(amount, comment, isPercentage, paymentType);
        this.additionalPrices.push(additionalPrice);
    }

    @action
    public removeAdditionalPrice(amount: number, comment: string, isPercentage: boolean, paymentType?: PaymentType) {
        const additionalPrices = this.additionalPrices.filter(t => 
            t.amount !== amount || t.comment !== comment || t.isPercentage !== isPercentage || t.paymentType !== paymentType);
        this.additionalPrices = [...additionalPrices];
    }
}

export default AddPaymentStore;